import {
  AfterViewInit,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { Router } from '@angular/router';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss/layout';
import {
  IAccount,
  IProduct,
  AccountOverviewResponse,
  AccountOverviewService
} from '../../services/account-overview.service';
import { MenuTriggerService } from '../../../shared/services/menu-trigger.service';
import { DialogStateService } from '../../../dialogs/services/dialog-state.service';
import { PortfolioService } from '../../../shared/services/portfolio.service';
import { PortfolioType } from '../../../shared/emuns/portfolio-type';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { AccountDetailsService } from '../../../shared/services/account-details.service';
import { FullPortfolioViewService } from '../../../shared/services/full-portfolio-view.service';
import { InitialViewService } from '../../../shared/services/initial-view.service';
import { MatLegacyMenuTrigger } from '@angular/material/legacy-menu';

@Component({
  selector: 'ab-accounts-overview',
  templateUrl: './accounts-overview.component.html',
  styleUrls: ['./accounts-overview.component.scss']
})
export class AccountsOverviewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() rendering: ComponentRendering;
  @ViewChildren(MatLegacyMenuTrigger)
  menuTriggers: QueryList<MatLegacyMenuTrigger>;

  public portfolio: IAccount;
  public isLoading = true;
  public isRetail = false;
  public portfolioChanged = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private accountOverviewService: AccountOverviewService,
    private menuTriggerService: MenuTriggerService,
    private dialogStateService: DialogStateService,
    private portfolioService: PortfolioService,
    private accountDetailsService: AccountDetailsService,
    private fullPortfolioViewService: FullPortfolioViewService,
    private initialViewService: InitialViewService
  ) {}

  @HostListener('document:click', ['$event'])
  documentClick(event) {
    this.menuTriggerService.documentEvent(event);
  }

  ngOnInit() {
    this.portfolioService.portfolioChanged.subscribe((hasChanged) => {
      this.portfolioChanged = hasChanged;
    });
    this.getAccountOverview();
    this.setPortfolioType();
  }

  ngAfterViewInit() {
    this.menuTriggerService.afterViewInit(this.menuTriggers);
  }

  ngOnDestroy() {
    this.menuTriggerService.unsubscribeFromAction();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  setPortfolioType() {
    this.subscriptions.push(
      this.fullPortfolioViewService.getFullPortfolioView().subscribe(() => {
        this.subscriptions.push(
          this.portfolioService
            .getSelectedPortfolio()
            .subscribe((portfolios) => {
              this.isRetail =
                PortfolioType[portfolios[0]?.portfolioType] ===
                PortfolioType.Retail;
            })
        );
      })
    );
  }

  getAccountOverview() {
    this.subscriptions.push(
      this.initialViewService
        .getAccounts()
        .pipe(first())
        .subscribe(
          (accountsOverview) => {
            if (accountsOverview && !this.portfolioChanged) {
              this.handleResponse(accountsOverview);
            } else {
              this.subscriptions.push(
                this.accountOverviewService.getAccountOverview().subscribe(
                  (response) => {
                    this.handleResponse(response);
                  },
                  () => (this.isLoading = false)
                )
              );
            }
          },
          () => (this.isLoading = false)
        )
    );
  }

  handleResponse(response: AccountOverviewResponse) {
    this.portfolio = response?.portfolios?.[0];
    if (!this.accountDetailsService.fromLogin && this.portfolio) {
      this.subscriptions.push(
        this.fullPortfolioViewService.getFullPortfolioView().subscribe(() => {
          this.accountDetailsService
            .getDetails()
            .subscribe((accounts: { [key: string]: IProduct }) => {
              this.isLoading = false;
              this.setAccountBalance(accounts);
            });
        })
      );
    } else {
      this.isLoading = false;
    }

    if (this.portfolio) {
      this.portfolio.products.sort((first: IProduct, second: IProduct) => {
        if (first.status === 'open' && first.accountActions != null) return -1;
        else return 1;
      });
    }
  }

  setAccountBalance(accounts: { [key: string]: IProduct }) {
    this.portfolio.products.forEach((product) => {
      const currentAccount = accounts[product.accountId];
      if (currentAccount) {
        product.visible = currentAccount.visible;
        product.accountName = currentAccount.accountName;
        product.bookBalance = currentAccount.bookBalance;
        product.availableBalance = currentAccount.availableBalance;
      }
    });
  }

  public openDialog(portfolio: IAccount) {
    this.dialogStateService
      .openDialog('BankAccountSetting', JSON.stringify(portfolio))
      .subscribe((result) => {
        if (result) {
          this.getAccountOverview();
        }
      });
  }

  public openUrl(url: string) {
    // TODO, change Portfolio ID
    let urlSegments = url.split('?'),
      uri = urlSegments[0],
      queryParams = urlSegments[1].split('&'),
      accountId = queryParams[0].split('id=')[1],
      mode = queryParams[1] ? queryParams[1].split('=')[1] : '';

    this.router.navigate([uri], {
      queryParams: {
        fromAccount: accountId,
        mode: mode
      }
    });
  }
}
